import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
} from '@angular/core';

import {
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
} from '@web/shared/ui';
import { Observable } from 'rxjs';
import { Guid } from '@web/utils';
import { AdminSubscriptionDataSourceService } from '../../services/admin-subscription-data-source.service';
import { AdminSubscriptionOrderCollectionItem } from '../../models/admin-subscription-order-collection-item';
import { AdminSubscriptionOrderCollectionFilter } from '../../models/admin-subscription-order-collection-filter';
import { AdminSubscriptionOrderListCustomCellTemplateComponent } from '../../components/admin-subscription-order-list-custom-cell-template/admin-subscription-order-list-custom-cell-template.component';
import { AdminSubscriptionOrderTypeProviderService } from '../../providers/admin-subscription-order-type-provider.service';
import { OrderStatusProviderService } from '@web/shared/module/shop';

@Component({
  selector: 'app-admin-subscription-order-list',
  imports: [
    TableComponent,
    AdminSubscriptionOrderListCustomCellTemplateComponent,
  ],
  templateUrl: './admin-subscription-order-list.component.html',
  styleUrl: './admin-subscription-order-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminSubscriptionOrderListComponent implements OnInit {
  public readonly id = input.required<Guid>();

  /* prettier-ignore */
  protected config!: TableConfig<AdminSubscriptionOrderCollectionItem, AdminSubscriptionOrderCollectionFilter>;

  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminSubscriptionOrderCollectionItem, AdminSubscriptionOrderCollectionFilter>).create();
  /* prettier-ignore */
  private readonly subscriptionDataSource = inject(AdminSubscriptionDataSourceService);
  private readonly orderStatusProvider = inject(OrderStatusProviderService);
  /* prettier-ignore */
  private readonly subscriptionOrderTypeProvider = inject(AdminSubscriptionOrderTypeProviderService);

  public ngOnInit(): void {
    this.buildTable();
  }

  /* prettier-ignore */
  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .filters((builder) => [
        builder //
          .initText('number')
          .build(),

        builder //
          .initNumber('totalItems')
          .build(),

        builder //
          .initMultiSelect('status')
          .dictionary(() => this.orderStatusProvider.selectItems())
          .build(),

        builder //
          .initMultiSelect('type')
          .dictionary(() => this.subscriptionOrderTypeProvider.selectItems())
          .build(),

        builder //
          .initNumber('totalPrice')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .columns((builder) => [
        builder //
          .initOrdinal()
          .build(),

        builder //
          .initCustom('number')
          .label('Numer')
          .width('10rem')
          .sortKey('number')
          .filterKey('number')
          .build(),

        builder //
          .initCustom('totalPrice')
          .label('Kwota')
          .width('10rem')
          .sortKey('totalPrice')
          .filterKey('totalPrice')
          .build(),

        builder //
          .initCustom('status')
          .width('12rem')
          .label('Status')
          .sortKey('status')
          .filterKey('status')
          .build(),

        builder //
          .initCustom('type')
          .width('12rem')
          .label('Rodzaj zamówienia')
          .sortKey('type')
          .filterKey('type')
          .build(),

        builder //
          .initDate('createdAt')
          .width('12rem')
          .label('Data zamówienia')
          .sortKey('createdAt')
          .filterKey('createdAt')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminSubscriptionOrderCollectionFilter>,
        ): Observable<TableDataPayload<AdminSubscriptionOrderCollectionItem>> =>
          this.subscriptionDataSource.fetchSubscriptionOrderCollection({
            id: this.id(),
            lazyLoad,
          }),
      })
      .build();
  }
}
